import { createFocusTrap } from 'focus-trap';
import { module } from 'modujs';
import { html } from '../utils/environment';

/**
 * Generic component to display a modal.
 *
 */
export default class Modal extends module {

    static get settings() {
        return {
            ACTIVE_CLASS: 'is-open',
        }
    }

    /**
     * Creates a new Modal.
     *
     * @param  {object} options          - The module options.
     * @param  {string} options.name     - The module class name.
     * @param  {string} options.dataName - The module data attribute name.
     * @throws {TypeError} If the class does not have an active CSS class defined.
     */
    constructor(options) {
        super(options);


        this.moduleName = options.name;

        this.events = {
            click: {
                close: 'close',
            },
        };

        this.closeBind = (e) => {
            if (e.key === 'Escape') {
                this.close();
            }
        }

        this.toggler = null;
        this.focusTrapOptions = {
            /**
             * There is a delay between when the class is applied
             * and when the element is focusable
             */
            checkCanFocusTrap: (trapContainers) => {
                const results = trapContainers.map((trapContainer) => {
                    return new Promise((resolve) => {
                        const interval = setInterval(() => {
                            if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                                resolve();
                                clearInterval(interval);
                            }
                        }, 5);
                    });
                });

                // Return a promise that resolves when all the trap containers are able to receive focus
                return Promise.all(results);
            },

            onActivate: () => {
                this.el.classList.add(Modal.settings.ACTIVE_CLASS);
                this.el.setAttribute('aria-hidden', false)

                setTimeout(() => {
                    this.$('close')[0].focus()
                }, 300)
            },

            onPostActivate: () => {
                if (this.toggler) {
                    this.toggler.setAttribute('aria-expanded', true);
                }
            },

            onDeactivate: () => {
                this.el.classList.remove(Modal.settings.ACTIVE_CLASS);
                this.el.setAttribute('aria-hidden', true)
            },

            onPostDeactivate: () => {
                if (this.toggler) {
                    this.toggler.setAttribute('aria-expanded', false);
                    this.toggler = null;
                }
            },
        };

        this.isOpen = false
    }

    init() {
        this.focusTrap = createFocusTrap(this.el, this.focusTrapOptions);

        document.addEventListener('keyup', this.closeBind)

        this.onInit?.()
    }

    /**
     * @param {HTMLButtonElement} toggler - The toggle element.
     */
    toggle(toggler) {
        if (toggler) {
            this.toggler = toggler;
        }

        if (this.el.classList.contains(Modal.settings.ACTIVE_CLASS)) {
            this.close();
        } else {
            this.open();
        }
    }

    open(args) {
        if (this.isOpen) return

        this.isOpen = true

        this.focusTrap?.activate?.();

        this.onOpen?.(args)
    }

    close(args) {
        if (!this.isOpen) return

        this.isOpen = false

        this.focusTrap?.deactivate?.();

        this.onClose?.(args)
    }

    destroy() {
        this.focusTrap?.deactivate?.({
            returnFocus: false
        });

        document.removeEventListener('keyup', this.closeBind)

        this.onDestroy?.()

        super.destroy();
    }
}
